import {
	Box,
	Button,
	IconButton,
	Stack,
	Typography,
	useTheme,
} from "@mui/material";
import { parseISO } from "date-fns";
import {
	ArrowLeftRight,
	Calculator,
	FilePen,
	FileSearch,
	type LucideIcon,
	MessageCircle,
	RefreshCw,
	X,
} from "lucide-react";
import { type FC, useMemo } from "react";
import { useSwipeable } from "react-swipeable";
import {
	AlertBoxActionEnum,
	AlertBoxDismissModeEnum,
	type PortfolioAlertBoxDTO,
} from "web_myplann/Infrastructure/Api/Api";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { getActionSubtext } from "web_myplann/NotificationCenter/EventAlert/actionSubtext";
import {
	getAlertTone,
	getAlertToneKey,
} from "web_myplann/NotificationCenter/EventAlert/alertTone";
import { EventActionButton } from "web_myplann/NotificationCenter/EventAlert/EventActionButton";
import { getEventVariant } from "web_myplann/NotificationCenter/EventAlert/eventClassification";
import {
	getDaysUntilEvent,
	getEventTense,
} from "web_myplann/NotificationCenter/EventAlert/eventTiming";
import { formatAlertDate } from "web_myplann/NotificationCenter/EventAlert/formatAlertDate";
import { parseAlertActionStates } from "web_myplann/NotificationCenter/EventAlert/parseAlertActionStates";
import type { EventClassificationEnum } from "web_myplann/NotificationCenter/EventAlert/types";
import { IconBox } from "web_myplann/Shared/Components/IconBox";

const actionIcon: Record<AlertBoxActionEnum, LucideIcon> = {
	[AlertBoxActionEnum.TalkToAgent]: MessageCircle,
	[AlertBoxActionEnum.CompareRates]: ArrowLeftRight,
	[AlertBoxActionEnum.CheckConditions]: FileSearch,
	[AlertBoxActionEnum.RenewInsurance]: RefreshCw,
	[AlertBoxActionEnum.RecalculateInsurance]: Calculator,
	[AlertBoxActionEnum.SignNewTravelInsurance]: FilePen,
};

const actionLabel = {
	[AlertBoxActionEnum.TalkToAgent]: Resources.EventAlert.Actions.contactAdvisor,
	[AlertBoxActionEnum.CompareRates]: Resources.EventAlert.Actions.compareRates,
	[AlertBoxActionEnum.CheckConditions]:
		Resources.EventAlert.Actions.checkConditions,
	[AlertBoxActionEnum.RenewInsurance]:
		Resources.EventAlert.Actions.renewInsurance,
	[AlertBoxActionEnum.RecalculateInsurance]:
		Resources.EventAlert.Actions.recalculateInsurance,
	[AlertBoxActionEnum.SignNewTravelInsurance]:
		Resources.EventAlert.Actions.arrangeNew,
};

/** Icon tile (32px) + gap (10px): the description aligns with the title. */
const DESCRIPTION_INDENT = "42px";

type Props = {
	alert: PortfolioAlertBoxDTO;
	classification: EventClassificationEnum;
	onAction: (action: AlertBoxActionEnum) => void;
	onDismiss: (mode: AlertBoxDismissModeEnum) => void;
};

export const EventAlertBox: FC<Props> = ({
	alert,
	classification,
	onAction,
	onDismiss,
}) => {
	const { palette, spacingTokens, lineHeight } = useTheme();
	const { t } = useResource();

	const { primaryAction, secondaryAction } = alert;
	const content = Resources.EventAlert.Content[classification];
	const variant = getEventVariant(alert.eventType);
	const actionStates = useMemo(() => parseAlertActionStates(alert), [alert]);

	const today = new Date();
	const tense = getEventTense(alert.eventDate, today);
	const days = getDaysUntilEvent(alert.eventDate, today);

	const interpolation = {
		count: days,
		days: t(Resources.EventAlert.inDays, { count: days }),
		daysCap: t(Resources.EventAlert.inDaysCapitalized, { count: days }),
		date: formatAlertDate(parseISO(alert.eventDate)),
	};

	const titleProxy = {
		future: content.titleFuture,
		today: content.titleToday,
		past: content.titlePast,
	}[tense];
	const textProxy = {
		future: content.textFuture,
		today: content.textToday,
		past: content.textPast,
	}[tense];

	const toneKey = getAlertToneKey(tense, variant);
	const tone = getAlertTone(palette, toneKey);
	const VariantIcon = tone.Icon;

	const swipeHandlers = useSwipeable({
		onSwipedLeft: () => onDismiss(AlertBoxDismissModeEnum.Temporary),
		onSwipedRight: () => onDismiss(AlertBoxDismissModeEnum.Temporary),
		trackMouse: false,
	});

	return (
		<Box
			{...swipeHandlers}
			borderRadius="12px"
			bgcolor={tone.bg}
			border={`1px solid ${tone.border}`}
			overflow="hidden"
		>
			<Stack
				direction="row"
				alignItems="flex-start"
				gap={1}
				px={1.4}
				pt={spacingTokens.md}
				pb={spacingTokens.xs}
			>
				<IconBox bg={tone.iconBg} size={32} mt="2px">
					<VariantIcon size={16} color={tone.iconColor} aria-hidden />
				</IconBox>
				<Typography
					variant="bodyStrong"
					component="div"
					flex={1}
					minWidth={0}
					lineHeight={1.375}
					color={tone.titleColor}
				>
					{t(titleProxy, interpolation)}
				</Typography>
				<IconButton
					onClick={() => onDismiss(AlertBoxDismissModeEnum.Temporary)}
					aria-label={t(Resources.EventAlert.dismissAriaLabel)}
					sx={{
						width: 24,
						height: 24,
						p: 0,
						flexShrink: 0,
						mr: "-4px",
						mt: "-2px",
					}}
				>
					<X size={14} color={palette.grey[400]} aria-hidden />
				</IconButton>
			</Stack>

			<Typography
				variant="caption"
				component="p"
				color={palette.grey[600]}
				lineHeight={lineHeight.relaxed}
				px={1.4}
				pb={1}
				ml={DESCRIPTION_INDENT}
			>
				{t(textProxy, interpolation)}
			</Typography>

			{(primaryAction || secondaryAction) && (
				<Stack gap={spacingTokens.sm} px={1.4} pb={spacingTokens.md}>
					{primaryAction && (
						<EventActionButton
							label={t(actionLabel[primaryAction])}
							onClick={() => onAction(primaryAction)}
							variant="contained"
							ctaButtonColor={tone.ctaButtonColor}
							Icon={
								primaryAction === AlertBoxActionEnum.TalkToAgent
									? actionIcon[primaryAction]
									: undefined
							}
							subtext={getActionSubtext(actionStates[primaryAction], t)}
						/>
					)}
					{secondaryAction && (
						<EventActionButton
							label={t(actionLabel[secondaryAction])}
							onClick={() => onAction(secondaryAction)}
							variant="text"
							subtext={getActionSubtext(actionStates[secondaryAction], t)}
						/>
					)}
				</Stack>
			)}

			<Stack alignItems="center" px={1.4} pb={spacingTokens.md}>
				<Button
					variant="textLink"
					size="small"
					color="neutral"
					onClick={() => onDismiss(AlertBoxDismissModeEnum.Permanent)}
				>
					{t(Resources.EventAlert.dontNotify)}
				</Button>
			</Stack>
		</Box>
	);
};
